import { useMemo, useState } from 'react';
import type { FC, FocusEvent, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import ExitTicketIconPassed from '../../assets/exit-ticket-icon-xs.svg?react';
import ExitTicketIconTaken from '../../assets/exit-ticket-icon-taken-xs.svg?react';
import Fire from '../../assets/Fire.svg?react';
import type { SkillExitSlipProgress } from '../../models/ExitSlip';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import {
  summarizeSkillExitSlips,
  type ExitSlipTicketGroup,
} from '../../businessLogicService/StudentDashboardAux/skillExitSlipProgress';

interface Props {
  progress: SkillExitSlipProgress;
  // The skill this badge belongs to, used only to name it in the accessible label.
  skillName: string;
  // Route segment for the skill's own page (StudentSkillStates.skill.code). Needed because the panel
  // can offer its own way through to the skill, and the card's click handler never fires for it —
  // every pointer event inside the badge is swallowed here.
  skillCode: string;
  // Whether the student already has this skill (passed or inferred). Only the fire booster reads it,
  // and only to stay away: see isSkillOnFire.
  skillAlreadyEarned: boolean;
}

// The panel's bottom edge: the same warm-gray understroke the badge pill and the fire booster carry,
// which is what gives all three the design's slightly raised look.
const TOOLTIP_STROKE_COLOR = '#EBE4DC'; // stone-100

// The bubble's downward tail. Drawn with inline styles rather than Tailwind border utilities so the
// global PrimeReact card styles cannot reach it.
const tailStyle = {
  position: 'absolute' as const,
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '10px solid transparent',
  borderRight: '10px solid transparent',
  borderTop: `12px solid ${TOOLTIP_STROKE_COLOR}`,
};

interface GroupRowProps {
  group: ExitSlipTicketGroup;
  label: string;
  // The status chip, matching the collapsed result banner: a green check for a pass, a blue minus for
  // a ticket that was only sat.
  chip: 'passed' | 'taken';
}

// One line of the hover panel: "1 Passed [✓] 80%". Rendered only for a group that has tickets in it,
// so the panel never shows a hollow "0 Passed" row.
const GroupRow: FC<GroupRowProps> = ({ group, label, chip }) => {
  const color = chip === 'passed' ? 'tw-text-green-400' : 'tw-text-stone-700';
  return (
    <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-atkinson">
      <span className={`tw-text-base tw-font-bold tw-leading-5 ${color}`}>
        {group.count} {label}
      </span>
      {chip === 'passed' ? (
        <ExitTicketIconPassed className="tw-h-5 tw-w-5 tw-shrink-0" aria-hidden="true" />
      ) : (
        <ExitTicketIconTaken className="tw-h-5 tw-w-5 tw-shrink-0" aria-hidden="true" />
      )}
      <span className={`tw-text-sm tw-font-bold tw-leading-5 ${color}`}>{group.scorePercent}%</span>
    </p>
  );
};

/**
 * The exit-ticket badge on a unit-path skill card: the marker that this skill can be inferred from
 * exit tickets, carrying how many of them the student has already sat.
 *
 * Three parts, from the design:
 *  - the pill with the exit-ticket icon and an orange chip counting the tickets finished for this
 *    skill (passed or not — sitting them is what the chip counts),
 *  - the fire booster stacked above it, bouncing, once the student has passed the last lesson's
 *    ticket and has yet to earn the skill (see isSkillOnFire),
 *  - a panel on hover breaking those tickets into passed / taken with each group's score.
 *
 * The panel has two faces. Normally it is a read-out headed "Progress on Exit tickets". Once every
 * ticket the student sat has been passed (see isReadyForChallenge) it becomes a nudge — "Seems ready
 * to try challenge", with a "Go there!" link onto the skill's own page.
 *
 * The panel opens on hover and on focus, and toggles on click so it is reachable by keyboard and on
 * a touch screen. Every pointer event is stopped here: the card underneath navigates to the skill on
 * click, and reaching for the badge is not a request to leave the page — the only way through is the
 * panel's own link.
 */
export const DashboardCardExitSlipBadge: FC<Props> = ({ progress, skillName, skillCode, skillAlreadyEarned }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const handleBrainBreakNavigation = useBrainBreakNavigation();
  const { completedCount, passed, taken, isOnFire, isReadyForChallenge } = useMemo(
    () => summarizeSkillExitSlips(progress, skillAlreadyEarned),
    [progress, skillAlreadyEarned],
  );

  const panelId = `exit-slip-badge-panel-${progress.skillId.replace(/[^\w-]/g, '-')}`;

  // The card underneath navigates to the skill page on click, so nothing inside the badge may reach
  // it: reaching for the badge — or for the panel it opens — is not a request to leave the page.
  const swallowClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleBadgeClick = (event: MouseEvent) => {
    swallowClick(event);
    setIsPanelOpen(open => !open);
  };

  // The panel's own way through to the skill, offered once every ticket sat has been passed. The
  // wrapper already stops the event reaching the card, but the card's destination is where this
  // should land anyway — so it goes the same route, brain break included.
  const handleGoToSkill = (event: MouseEvent) => {
    event.stopPropagation();
    handleBrainBreakNavigation(event, () => navigate(`/skill/${skillCode}`), 'post-login');
  };

  // Focus is tracked on the wrapper rather than on the badge itself so the panel survives a tab into
  // its "Go there!" link: React's onFocus/onBlur ride focusin/focusout, which bubble, so the wrapper
  // sees focus anywhere inside it. focusout also fires when focus merely hops between two children,
  // hence the relatedTarget check — only focus leaving the badge entirely closes the panel.
  const handleBadgeBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsPanelOpen(false);
    }
  };

  return (
    <div
      className="tw-absolute tw-left-6 tw-top-[-14px] tw-z-20"
      onMouseEnter={() => setIsPanelOpen(true)}
      onMouseLeave={() => setIsPanelOpen(false)}
      onFocus={() => setIsPanelOpen(true)}
      onBlur={handleBadgeBlur}
      onClick={swallowClick}
    >
      {/* Sits above the pill without moving it: the pill stays in flow, so the badge is in the same
          place on the card whether or not the student is on fire. */}
      {isOnFire && !isPanelOpen && (
        <div className="tw-absolute tw-bottom-full tw-left-1/2 tw-translate-x-[-50%] tw-pb-1">
          <motion.div
            // The bounce is the whole point of the booster — it has to catch the eye on a path full
            // of static cards — so it loops rather than playing once. `y` only, and on this inner
            // element, so it cannot fight the centering translate above.
            animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span className="tw-p-[2px] tw-border-solid tw-border-stone-100 tw-border-t-[2px] tw-border-x-[2px] tw-border-b-[4px] tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-0 tw-shadow-md">
              {/* The asset's artboard is wider than the flame it draws, so it is scaled up slightly
                  and clipped by the rounded box to fill it. */}
              <Fire className="tw-h-6 tw-w-6" aria-hidden="true" />
            </span>
          </motion.div>
        </div>
      )}

      <button
        type="button"
        aria-label={`${completedCount} exit ${completedCount === 1 ? 'ticket' : 'tickets'} completed for ${skillName}${
          isOnFire ? ', on fire' : ''
        }`}
        aria-expanded={isPanelOpen}
        aria-describedby={isPanelOpen ? panelId : undefined}
        onClick={handleBadgeClick}
        onFocus={() => setIsPanelOpen(true)}
        onBlur={() => setIsPanelOpen(false)}
        style={{
          background: isOnFire ? 'linear-gradient(180deg, #E33434 0%, #FF7C34 100%)' : '#5776FC',
        }} // above the card's shadow, below the panel
        className={`tw-border-solid ${
          isOnFire ? 'tw-border-[#F8946D]' : 'tw-border-[#9FABFD]'
        } tw-border-t-[1px] tw-border-x-[1px] tw-border-b-[2px]  tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-lg tw-border-0 tw-px-2 tw-py-1 tw-shadow-md tw-transition-colors tw-duration-150 ${
          isPanelOpen ? 'tw-bg-blue-500' : 'tw-bg-neutral-0'
        } `}
      >
        <ExitTicketIconPassed className="tw-h-3.5 tw-w-3.5 tw-rounded-[2px]" aria-hidden="true" />
        <span className="tw-inline-flex tw-h-4 tw-min-w-[1rem] tw-items-center tw-justify-center tw-rounded-[4px] tw-bg-transparent tw-px-1 tw-font-atkinson tw-text-sm tw-font-bold tw-leading-none tw-text-white">
          {completedCount}
        </span>
      </button>

      <AnimatePresence>
        {isPanelOpen && (
          // Centered on the badge and growing upward out of its tail. Wider than the badge by design,
          // so on the leftmost cards it reaches past the card's edge — the unit path is centered on
          // the page with room either side.
          //
          // The centering is motion's own `x` rather than a Tailwind translate, and this element is
          // AnimatePresence's direct child: a positioning wrapper in between would both have its
          // transform overwritten by the scale animation and leave the exit animation unplayed,
          // because AnimatePresence only defers the unmount of a motion component it owns directly.
          // The gap above the badge is `mb-3` against `bottom-full` for the same reason.
          <motion.div
            initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.9, x: '-50%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%' }}
            exit={{ opacity: 0, scale: reduceMotion ? 1 : 0.9, x: '-50%' }}
            transition={{ duration: 0.15 }}
            style={{ originX: 0.5, originY: 1 }}
            id={panelId}
            role="tooltip"
            // border-solid is not optional: Tailwind leaves border-style at none, so a width on its
            // own would draw nothing.
            className="tw-absolute tw-bottom-full tw-left-1/2 tw-z-50 tw-mb-3 tw-w-[240px] tw-rounded-xl tw-border-2 tw-border-b-4 tw-border-solid tw-border-stone-100 tw-bg-stone-0 tw-p-6"
          >
            <h4
              className={`tw-m-0 tw-mb-3 tw-text-center tw-font-atkinson tw-text-lg tw-text-neutral-950 ${
                isReadyForChallenge ? 'tw-font-bold' : 'tw-font-normal'
              }`}
            >
              {isReadyForChallenge ? 'Seems ready to try challenge' : 'Progress on Exit tickets'}
            </h4>
            {completedCount === 0 ? (
              <p className="tw-m-0 tw-text-center tw-font-atkinson tw-text-base tw-text-neutral-600">None taken yet</p>
            ) : (
              <div className="tw-mx-auto tw-flex tw-w-max tw-flex-col tw-gap-2">
                {passed.count > 0 && <GroupRow group={passed} label="Passed" chip="passed" />}
                {taken.count > 0 && <GroupRow group={taken} label="Taken" chip="taken" />}
              </div>
            )}
            {/* Only offered on a clean sheet: with a ticket still unpassed the skill is not what the
                design is pointing them at, so the panel stays a read-out. */}
            {isReadyForChallenge && (
              <button
                type="button"
                onClick={handleGoToSkill}
                className="tw-mt-3 tw-block tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-center tw-font-atkinson tw-text-base tw-font-bold tw-text-blue-500 tw-underline tw-decoration-2 tw-underline-offset-4"
              >
                Go there!
              </button>
            )}
            <span style={tailStyle} aria-hidden="true" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardCardExitSlipBadge;
